/**
 * v3.admin.cartRetentionPolicies — Wave 6.2 admin write surface for the
 * `CartRetentionPolicies` table (composite PK `(OriginRole, ResolutionCode)`,
 * default 7 years = 2555 days).
 *
 * Routes (server: PCBJxChangeApi/Functions/AdminCartRetentionFunction.cs,
 * landing alongside companion CART-RESOLUTION-BACKEND build):
 *   POST   /v3/admin/cart-retention-policies
 *   DELETE /v3/admin/cart-retention-policies/{originRole}/{resolutionCode}
 *
 * All routes require the `x-config-seed-bypass: <bypassKey>` header (mirrors
 * the Wave 6.1 admin sub-clients in v3-admin-products.ts).
 *
 * Composed onto `AdminApi.cartRetentionPolicies` from `client.ts` after
 * construction (no ctor change in v3-admin.ts, same isolation pattern as the
 * products write surface).
 */

import type {
  RequestOptions,
  CartRetentionPolicy,
  CartRetentionPolicyUpsertInput,
  CartOriginRole,
  CartResolutionCode,
  ApiResult,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import type { AdminWriteResult } from './admin.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

class CartRetentionPoliciesAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * List the full retention-policy catalog. Read endpoint is admin-scoped
   * but does not require the bypass key.
   */
  public list(opts?: RequestOptions): Promise<ApiResult<CartRetentionPolicy[]>> {
    return this.http.get<ApiResult<CartRetentionPolicy[]>>(
      '/v3/admin/cart-retention-policies',
      { ...opts },
    );
  }

  public upsert(
    bypassKey: string,
    body: CartRetentionPolicyUpsertInput,
    opts?: RequestOptions,
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<AdminWriteResult>('/v3/admin/cart-retention-policies', {
      ...opts,
      headers,
      body,
    });
  }

  public remove(
    bypassKey: string,
    originRole: CartOriginRole,
    resolutionCode: CartResolutionCode,
    opts?: RequestOptions,
  ): Promise<AdminWriteResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    const path =
      `/v3/admin/cart-retention-policies/${encodeURIComponent(originRole)}/${encodeURIComponent(resolutionCode)}`;
    return this.http.del<AdminWriteResult>(path, { ...opts, headers });
  }
}

/**
 * Composite — exposed at `client.retail.admin.cartRetentionPolicies`.
 */
export class AdminCartRetentionApi {
  public readonly policies: CartRetentionPoliciesAdminApi;

  public constructor(http: ApiHttpClient) {
    this.policies = new CartRetentionPoliciesAdminApi(http);
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Module augmentation — extend AdminApi with `.cartRetentionPolicies`.
// ─────────────────────────────────────────────────────────────────────────

declare module './admin.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface AdminApi {
    /** Wave 6.2 — CartRetentionPolicies typed write surface. */
    readonly cartRetentionPolicies: AdminCartRetentionApi;
  }
}
